import React, { useState } from 'react';
import { Volume2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** One-tap "wipe every cached ElevenLabs narration" button.
 *  Talks to POST /api/admin/tts/clear-cache. Also deletes the R2 MP3s so
 *  storage doesn't fill up with orphans.
 */
const ClearTTSCacheButton = ({ token }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const clear = async () => {
    if (busy) return;
    const ok = window.confirm(
      'Wipe every cached article narration?\n\nThis deletes both the DB references AND the MP3 files in R2. The next time someone plays an article, it will be re-generated with the current ElevenLabs voice. This cannot be undone.',
    );
    if (!ok) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await fetch(`${API}/api/admin/tts/clear-cache`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Clear failed.');
      setMsg(
        `Cleared ${data.modified} article${data.modified === 1 ? '' : 's'}. ` +
        `R2 objects deleted: ${data.r2_deleted}${data.r2_failed ? ` (failed: ${data.r2_failed})` : ''}.`,
      );
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="clear-tts-cache-card"
      className="border border-zinc-800 bg-zinc-900/40 rounded-lg p-4 mb-4"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Volume2 className="h-5 w-5 shrink-0 mt-0.5 text-red-400" />
          <div className="min-w-0">
            <p className="text-white font-black text-sm">ElevenLabs Narration Cache</p>
            <p className="text-zinc-400 text-xs leading-relaxed mt-0.5">
              Wipes every cached article narration (DB + R2 MP3s). Use this after swapping the
              ElevenLabs voice ID so the next play regenerates with the new voice.
            </p>
            {msg && (
              <p className="text-emerald-400 text-xs mt-2" data-testid="clear-tts-cache-msg">
                {msg}
              </p>
            )}
            {err && (
              <p className="text-red-400 text-xs mt-2" data-testid="clear-tts-cache-err">
                {err}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={clear}
          disabled={busy}
          data-testid="clear-tts-cache-btn"
          className="inline-flex items-center h-10 px-4 rounded font-black text-xs uppercase tracking-[0.25em] whitespace-nowrap bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white"
        >
          {busy ? 'Clearing…' : 'Clear cache'}
        </button>
      </div>
    </div>
  );
};

export default ClearTTSCacheButton;
